import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { CauseCard } from "@/project/components/cards";
import { causes, categories, animalTypes } from "@/project/data/sample";

export const Route = createFileRoute("/causes/")({
  head: () => ({
    meta: [
      { title: "Active donation causes — Animal Seva" },
      { name: "description", content: "Donate to specific causes: fodder, surgery, ambulance, rescue, water and infrastructure for animals in need." },
    ],
  }),
  component: CausesPage,
});

function CausesPage() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [animal, setAnimal] = useState("");
  const [sort, setSort] = useState<"urgency" | "newest" | "most">("urgency");

  const list = useMemo(() => {
    let arr = causes.filter((c) => {
      if (q && !c.title.toLowerCase().includes(q.toLowerCase())) return false;
      if (cat && c.category !== cat) return false;
      if (animal && c.animalType !== animal) return false;
      return true;
    });
    if (sort === "urgency") {
      const r = { Critical: 0, Urgent: 1, Normal: 2 } as const;
      arr = [...arr].sort((a, b) => r[a.urgency] - r[b.urgency]);
    } else if (sort === "most") {
      arr = [...arr].sort((a, b) => b.raised - a.raised);
    }
    return arr;
  }, [q, cat, animal, sort]);

  return (
    <SiteShell>
      <section className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Causes</p>
          <h1 className="mt-2 font-display text-3xl font-semibold sm:text-4xl text-[var(--maroon-deep)]">Active donation causes</h1>
          <p className="mt-3 max-w-2xl text-foreground/70">Pick a specific cause — fodder, surgery, ambulance, rescue or shelter — and watch your impact unfold.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-8 grid gap-3 rounded-2xl border border-border/60 bg-card p-4 shadow-[var(--shadow-soft)] md:grid-cols-12">
          <div className="md:col-span-4 flex items-center gap-2 rounded-full border border-border bg-background px-3">
            <Search className="h-4 w-4 text-foreground/70" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search causes…"
                   className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="md:col-span-3 rounded-full border border-border bg-background px-4 py-2.5 text-sm">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={animal} onChange={(e) => setAnimal(e.target.value)} className="md:col-span-3 rounded-full border border-border bg-background px-4 py-2.5 text-sm">
            <option value="">All animals</option>
            {animalTypes.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value as any)} className="md:col-span-2 rounded-full border border-border bg-background px-4 py-2.5 text-sm">
            <option value="urgency">Most urgent</option>
            <option value="most">Most donated</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => <CauseCard key={c.id} cause={c} />)}
        </div>
      </section>
    </SiteShell>
  );
}